import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search as SearchIcon, Book, User, Users, AlertCircle, RotateCcw, Sparkles, ChevronLeft, ChevronRight, Hash, X, Clock, Lightbulb, Camera, Loader2, Globe } from 'lucide-react';
import { useRecommendedUsers } from '@/hooks/useRecommendedUsers';
import { useSearchTracking } from '@/hooks/useSearchTracking';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DinoAvatar } from '@/components/DinoAvatar';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { BookCard } from '@/components/BookCard';
import { CoverImage } from '@/components/CoverImage';
import { ReviewTBRButtons } from '@/components/ReviewTBRButtons';
import { BarcodeScanner } from '@/components/BarcodeScanner';
import { useToast } from '@/hooks/use-toast';
import { normalizeOpenLibraryId, searchOpenLibrary } from '@/lib/searchUtils';
import { BookResult, UserResult } from '@/types/search';
import { SEARCH_LANGUAGES } from '@/lib/constants';
import { useRecentSearches } from '@/hooks/useRecentSearches';
import { isISBNQuery, validateISBN } from '@/utils/isbnUtils';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
} from "@/components/ui/pagination";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

export default function Search() {
  const [query, setQuery] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('q') || '';
  });
  const [bookResults, setBookResults] = useState<BookResult[]>([]);
  const [externalBookResults, setExternalBookResults] = useState<BookResult[]>([]);
  const [userResults, setUserResults] = useState<UserResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingExternal, setLoadingExternal] = useState(false);
  const [localCount, setLocalCount] = useState(0);
  const [externalCount, setExternalCount] = useState(0);
  const [searchSource, setSearchSource] = useState<'local' | 'external' | 'both'>('local');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchType, setSearchType] = useState<'title' | 'author' | 'isbn'>('title');
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('lang') || 'eng';
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [activeTab, setActiveTab] = useState<'books' | 'people' | 'suggested'>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    if (tab === 'people' || tab === 'suggested') return tab;
    return 'books';
  });
  const [searchError, setSearchError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<BookResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsType, setSuggestionsType] = useState<'personalized' | 'popular'>('popular');
  const [showSearchTips, setShowSearchTips] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: recommendations, isLoading: recommendationsLoading } = useRecommendedUsers();
  const [followingLoading, setFollowingLoading] = useState<Record<string, boolean>>({});
  const { recentSearches, addRecentSearch, clearRecentSearches } = useRecentSearches();
  const { trackImpression, trackClick, trackTBRAdd, trackTrendingSearch } = useSearchTracking();
  const sessionIdRef = useRef<string>(crypto.randomUUID());
  
  const isDebug = new URLSearchParams(window.location.search).has('debug');

  // Track latest request IDs to drop stale responses
  const bookReqIdRef = useRef(0);
  const userReqIdRef = useRef(0);

  // Memoize book results with stable keys to prevent unnecessary re-renders
  const memoizedBookResults = useMemo(() => 
    bookResults.map((book, index) => ({
      ...book,
      stableKey: book.id || book.open_library_id || `search-${index}-${book.title}`,
    })),
    [bookResults]
  );


  const searchBooks = useCallback(async (q: string, page: number, lang: string, type: 'title' | 'author' | 'isbn', source: 'local' | 'external' = 'local') => {
    const requestId = ++bookReqIdRef.current;
    
    if (isDebug) console.log(`[BOOK SEARCH] Starting search:`, { q, page, lang, type, source, requestId });
    
    // Clear old results immediately to prevent flashing stale data
    if (source === 'local' && page === 1) {
      setBookResults([]);
    } else if (source === 'external') {
      setExternalBookResults([]);
    }
    
    if (source === 'external') {
      setLoadingExternal(true);
    } else {
      setLoading(true);
    }
    setSearchError(null);

    try {
      const { data, error } = await supabase.functions.invoke('search-books', {
        body: { 
          query: q, 
          type, 
          page, 
          limit: 20, 
          language: lang, 
          source,
          sessionId: sessionIdRef.current,
          debug: isDebug
        },
      });

      if (error) {
        throw error;
      }

      if (isDebug) console.log(`[BOOK SEARCH] Received results:`, { requestId, currentReqId: bookReqIdRef.current, resultsCount: data?.results?.length, source });

      if (requestId === bookReqIdRef.current) {
        if (source === 'external') {
          if (isDebug) console.log(`[BOOK SEARCH] Setting external results:`, data.results?.length);
          setExternalBookResults(data.results || []);
          setExternalCount(data.externalCount || 0);
          
          // Track impressions for external results
          (data.results || []).forEach((book: BookResult, index: number) => {
            if (book.id || book.open_library_id) {
              trackImpression({
                query: q,
                searchType: type,
                bookId: book.id || book.open_library_id!,
                position: index,
                source: 'openlibrary',
              });
            }
          });
        } else {
          if (isDebug) console.log(`[BOOK SEARCH] Setting local results:`, data.results?.length);
          setBookResults(data.results || []);
          setLocalCount(data.total || data.localCount || 0);
          setExternalCount(data.externalCount || 0);
          setHasMoreResults(data.hasMore || false);
          
          // Track impressions for local results
          (data.results || []).forEach((book: BookResult, index: number) => {
            if (book.id) {
              trackImpression({
                query: q,
                searchType: type,
                bookId: book.id,
                position: index,
                source: book.isLocal ? 'local' : 'openlibrary',
              });
            }
          });
          
          // Track trending search
          trackTrendingSearch(q);

          // Automatically fall back to external sources when no local results are found
          const noLocalResults = !data?.results || data.results.length === 0;
          if (noLocalResults && q.trim()) {
            setSearchSource('external');
            searchBooks(q, page, lang, type, 'external');
          }
        }
        addRecentSearch(q, type);
      } else {
        if (isDebug) console.log(`[BOOK SEARCH] Dropping stale response:`, { requestId, currentReqId: bookReqIdRef.current });
      }
    } catch (error) {
      console.error('[BOOK SEARCH] Error:', error);
      if (requestId === bookReqIdRef.current) {
        setSearchError(error instanceof Error ? error.message : 'Search failed');
        if (source === 'external') {
          setExternalBookResults([]);
        } else {
          setBookResults([]);
        }
      }
    } finally {
      if (source === 'external') {
        setLoadingExternal(false);
      } else {
        setLoading(false);
      }
    }
  }, [addRecentSearch, isDebug]);

  const searchUsers = useCallback(async (searchQuery: string) => {
    const q = searchQuery.trim();
    if (!q) {
      setUserResults([]);
      setLoadingUsers(false);
      return;
    }

    // Increment request id to cancel stale responses
    userReqIdRef.current += 1;
    const requestId = userReqIdRef.current;

    setLoadingUsers(true);
    try {
      const { data: users } = await supabase
        .rpc('search_public_profiles', { 
          search_term: q, 
          excluding_user_id: user?.id 
        })
        .limit(20);

      // Drop stale response
      if (requestId !== userReqIdRef.current) return;

      if (users && users.length > 0) {
        // Batch check follow status for displayed users
        const { data: follows } = await supabase
          .from('follows')
          .select('following_id')
          .eq('follower_id', user?.id)
          .in('following_id', users.map(u => u.id));

        const followingIds = new Set(follows?.map(f => f.following_id) || []);
        const usersWithFollowStatus = users.map(u => ({
          ...u,
          isFollowing: followingIds.has(u.id),
        }));

        setUserResults(usersWithFollowStatus);
      } else {
        setUserResults([]);
      }
    } catch (error) {
      // Only report if this is the latest request
      if (requestId === userReqIdRef.current) {
        console.error('User search error:', error);
        toast({
          variant: "destructive",
          title: "Search failed",
          description: "There was an error searching for users.",
        });
      }
    } finally {
      if (requestId === userReqIdRef.current) {
        setLoadingUsers(false);
      }
    }
  }, [user?.id, toast]);

  // Sync URL with query, tab, and language
  useEffect(() => {
    const url = new URL(window.location.href);
    if (query) {
      url.searchParams.set('q', query);
    } else {
      url.searchParams.delete('q');
    }
    if (activeTab !== 'books') {
      url.searchParams.set('tab', activeTab);
    } else {
      url.searchParams.delete('tab');
    }
    if (selectedLanguage !== 'eng') {
      url.searchParams.set('lang', selectedLanguage);
    } else {
      url.searchParams.delete('lang');
    }
    const newUrl = url.toString();
    if (newUrl !== window.location.href) {
      window.history.replaceState({}, '', newUrl);
    }
  }, [query, activeTab, selectedLanguage]);

  // Debounced search - increased to 500ms to reduce flashing
  useEffect(() => {
    // Clear results immediately when query changes significantly
    if (activeTab === 'books' && query.trim().length === 0) {
      setBookResults([]);
      setExternalBookResults([]);
    }

    const timer = setTimeout(() => {
      if (activeTab === 'books') {
        if (query.trim()) {
          setCurrentPage(1);
          searchBooks(query, 1, selectedLanguage, searchType);
        } else {
          // Clear results if query is empty
          setBookResults([]);
          setExternalBookResults([]);
        }
      } else {
        // For people search, require at least 2 characters to reduce load/flashing
        if (!query || query.trim().length < 2) {
          setUserResults([]);
          setLoadingUsers(false);
          return;
        }
        searchUsers(query);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [query, activeTab, selectedLanguage, searchType]); // Removed searchBooks and searchUsers from deps

  const goToPage = (page: number) => {
    console.log(`[Pagination] Going to page: ${page}`);
    setCurrentPage(page);
    searchBooks(query, page, selectedLanguage, searchType);
    // Scroll to top of results
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearchTypeChange = (newType: 'title' | 'author' | 'isbn') => {
    setSearchType(newType);
    setCurrentPage(1);
    setSearchSource('local');
    // Clear both local and external results to prevent stale data
    setBookResults([]);
    setExternalBookResults([]);
  };

  const handleSearchExternal = () => {
    setSearchSource('external');
    searchBooks(query, 1, selectedLanguage, searchType, 'external');
  };

  const loadSuggestions = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke('search-suggestions');
      if (error) throw error;
      
      setSuggestions(data.suggestions || []);
      setSuggestionsType(data.type || 'popular');
    } catch (error) {
      console.error('Error loading suggestions:', error);
    }
  }, []);

  // Load suggestions on mount
  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  const handleBookClick = async (book: BookResult) => {
    try {
      // Track click event
      if (book.id || book.open_library_id) {
        trackClick(book.id || book.open_library_id!);
      }

      // If it's a local book, navigate directly
      if (book.isLocal && book.id) {
        navigate(`/book/${book.id}`);
        return;
      }

      const normalizedId = normalizeOpenLibraryId(book.open_library_id || '');
      if (!normalizedId) {
        throw new Error('Invalid book identifier');
      }
      
      // Check for existing book with multi-factor validation including canonical_isbn13
      const { data: existingBooks } = await supabase
        .from('books')
        .select('id, published_year, isbn13, isbn10, open_library_id, canonical_isbn13')
        .or(`open_library_id.eq.${normalizedId},open_library_id.eq./works/${normalizedId}${book.isbn13 ? `,isbn13.eq.${book.isbn13},canonical_isbn13.eq.${book.isbn13}` : ''}${book.isbn10 ? `,isbn10.eq.${book.isbn10}` : ''}`)
        .limit(5);

      // Validate it's actually the same book using multiple factors
      const matchingBook = existingBooks?.find(existing => {
        const yearMatch = !book.published_year || !existing.published_year || 
                          existing.published_year === book.published_year;
        const isbnMatch = (book.isbn13 && book.isbn13 === existing.isbn13) || 
                         (book.isbn10 && book.isbn10 === existing.isbn10) ||
                         (book.isbn13 && book.isbn13 === existing.canonical_isbn13);
        const olidMatch = existing.open_library_id === normalizedId || 
                         existing.open_library_id === `/works/${normalizedId}`;
        
        // Must match on OLID + year, OR have matching ISBN/canonical ISBN
        return (olidMatch && yearMatch) || isbnMatch;
      });

      if (matchingBook) {
        navigate(`/book/${matchingBook.id}`);
        return;
      }

      // Create new book with proper validation and English language default
      const bookData = {
        open_library_id: normalizedId,
        title: book.title || 'Untitled',
        subtitle: book.subtitle,
        authors: book.authors || [],
        published_year: book.published_year,
        cover_url: book.cover_url,
        isbn10: book.isbn10,
        isbn13: book.isbn13,
        synopsis: book.synopsis,
        source: 'openlibrary',
        language: selectedLanguage === 'eng' ? 'en' : selectedLanguage,
        enrichment_status: 'pending'
      };

      const { data: newBook, error } = await supabase
        .from('books')
        .insert(bookData)
        .select('id')
        .single();

      if (error) {
        // Handle duplicate ISBN constraint violation
        if (error.code === '23505' && error.message.includes('canonical_isbn13')) {
          // Try to find the existing book by ISBN
          const { data: existingByIsbn } = await supabase
            .from('books')
            .select('id')
            .or(`isbn13.eq.${book.isbn13},canonical_isbn13.eq.${book.isbn13}`)
            .limit(1)
            .single();
          
          if (existingByIsbn?.id && user) {
            // Automatically merge any data from the duplicate attempt
            const { cleanupDuplicateBook } = await import('@/utils/cleanupDuplicateBook');
            
            try {
              // Note: We don't have a duplicate book ID yet since insert failed,
              // but we'll navigate and show a message
              toast({
                title: "Found existing edition",
                description: "This book already exists in our database. Showing the existing entry.",
              });
            } catch (cleanupError) {
              console.error('[Search] Cleanup error:', cleanupError);
            }
            
            navigate(`/book/${existingByIsbn.id}`);
            return;
          }
        }
        throw error;
      }
      
      if (!newBook?.id) throw new Error('Failed to create book');

      navigate(`/book/${newBook.id}`);
    } catch (error) {
      console.error('Error handling book click:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "There was an error opening the book details.",
      });
    }
  };

  const retrySearch = () => {
    if (query) {
      setCurrentPage(1);
      searchBooks(query, 1, selectedLanguage, searchType);
    }
  };

  const handleFollowToggle = async (targetUser: any) => {
    if (!user) return;

    setFollowingLoading(prev => ({ ...prev, [targetUser.id]: true }));

    try {
      if (targetUser.isFollowing) {
        // Unfollow
        await supabase
          .from('follows')
          .delete()
          .eq('follower_id', user.id)
          .eq('following_id', targetUser.id);

        toast({
          title: "Unfollowed",
          description: `You unfollowed ${targetUser.name || targetUser.username}.`,
        });

        // Update local state
        queryClient.setQueryData(['recommendedUsers'], (old: any) => {
          if (!old) return old;
          const updateUser = (u: any) => u.id === targetUser.id ? { ...u, isFollowing: false } : u;
          return {
            mutualFollows: old.mutualFollows?.map(updateUser) || [],
            similarReaders: old.similarReaders?.map(updateUser) || [],
            trendingUsers: old.trendingUsers?.map(updateUser) || []
          };
        });
      } else {
        // Follow
        await supabase
          .from('follows')
          .insert({
            follower_id: user.id,
            following_id: targetUser.id,
          });

        toast({
          title: "Following",
          description: `You are now following ${targetUser.name || targetUser.username}.`,
        });

        // Update local state
        queryClient.setQueryData(['recommendedUsers'], (old: any) => {
          if (!old) return old;
          const updateUser = (u: any) => u.id === targetUser.id ? { ...u, isFollowing: true } : u;
          return {
            mutualFollows: old.mutualFollows?.map(updateUser) || [],
            similarReaders: old.similarReaders?.map(updateUser) || [],
            trendingUsers: old.trendingUsers?.map(updateUser) || []
          };
        });
      }
    } catch (error) {
      console.error('Follow toggle error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was an error updating follow status.",
      });
    } finally {
      setFollowingLoading(prev => ({ ...prev, [targetUser.id]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-background">

      <main className="container mx-auto px-4 py-8 space-y-6">
        {/* Unified Search Bar */}
        <div className="max-w-2xl mx-auto space-y-4">
          <div className="flex gap-2">
            {/* Search Type Selector */}
            <Select value={searchType} onValueChange={(value) => setSearchType(value as 'title' | 'author' | 'isbn')}>
              <SelectTrigger className="w-[140px] h-12">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="title">
                  <div className="flex items-center gap-2">
                    <Book className="h-4 w-4" />
                    Title
                  </div>
                </SelectItem>
                <SelectItem value="author">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Author
                  </div>
                </SelectItem>
                <SelectItem value="isbn">
                  <div className="flex items-center gap-2">
                    <Hash className="h-4 w-4" />
                    ISBN
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Unified Search Input */}
            <div className="relative flex-1">
              <SearchIcon className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder={
                  searchType === 'isbn' 
                    ? "Enter ISBN (10 or 13 digits)..." 
                    : `Search by ${searchType}...`
                }
                value={query}
                onChange={(e) => {
                  const value = e.target.value;
                  setQuery(value);
                  // Auto-validate ISBN as user types
                  if (searchType === 'isbn' && value.length >= 10) {
                    const { valid } = validateISBN(value);
                    if (!valid && value.length >= 13) {
                      // Show subtle error for invalid ISBN
                      console.log('Invalid ISBN format');
                    }
                  }
                }}
                onFocus={() => searchType !== 'isbn' && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                className="pl-10 pr-20 h-12 text-base"
              />
              {query && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setQuery('');
                    setBookResults([]);
                  }}
                  className="absolute right-2 top-2.5"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Scan Button (only for ISBN) */}
            {searchType === 'isbn' && (
              <Button
                variant="outline"
                onClick={() => setShowBarcodeScanner(true)}
                className="h-12 px-4"
                title="Scan barcode"
              >
                <Camera className="w-5 h-5" />
              </Button>
            )}
          </div>
            
          {/* Barcode Scanner Dialog */}
          <BarcodeScanner
            open={showBarcodeScanner}
            onClose={() => setShowBarcodeScanner(false)}
            onScan={(isbn) => {
              setQuery(isbn);
              setSearchType('isbn');
              toast({
                title: "ISBN Scanned",
                description: "Searching for book...",
              });
            }}
          />
            
          {/* Recent Searches */}
          {recentSearches.length > 0 && !query && (
            <Card className="border-muted/40">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5" />
                    Recent Searches
                  </p>
                  <Button variant="ghost" size="sm" onClick={clearRecentSearches} className="h-auto p-1">
                    <X className="w-3 h-3" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {recentSearches.map((search, idx) => (
                    <Badge
                      key={idx}
                      variant="secondary"
                      className="cursor-pointer hover:bg-accent"
                      onClick={() => {
                        setQuery(search.query);
                        setSearchType(search.type);
                      }}
                    >
                      {search.type === 'author' ? <User className="w-3 h-3 mr-1" /> : search.type === 'isbn' ? <Hash className="w-3 h-3 mr-1" /> : <Book className="w-3 h-3 mr-1" />}
                      {search.query}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
            
          {showSuggestions && !query && suggestions.length > 0 && activeTab === 'books' && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-card border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
              <div className="p-3 border-b">
                <p className="text-sm font-medium">
                  {suggestionsType === 'personalized' 
                    ? 'Books by authors you\'ve read' 
                    : 'Popular books'}
                </p>
              </div>
              <div className="p-2">
                {suggestions.map((book) => (
                  <button
                    key={book.id || book.open_library_id}
                    onClick={() => {
                      setQuery(book.title);
                      setShowSuggestions(false);
                    }}
                    className="w-full flex items-center gap-3 p-2 hover:bg-accent rounded-lg transition-colors text-left"
                  >
                    <CoverImage
                      bookId={book.id}
                      isbn13={book.isbn13}
                      isbn10={book.isbn10}
                      olid={normalizeOpenLibraryId(book.open_library_id || '')}
                      title={book.title}
                      author={book.authors?.[0]}
                      size="xs"
                      className="w-10 h-14 rounded shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{book.title}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {book.authors?.join(', ')}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="max-w-4xl mx-auto">
          <Tabs value={activeTab} onValueChange={(value) => {
            setActiveTab(value as 'books' | 'people' | 'suggested');
            // Update URL to reflect tab change
            const url = new URL(window.location.href);
            url.searchParams.set('tab', value);
            window.history.replaceState({}, '', url.toString());
          }}>
            <TabsList className="grid w-full grid-cols-3 max-w-md mx-auto">
              <TabsTrigger value="books" className="flex items-center gap-2">
                <Book className="w-4 h-4" />
                Books
              </TabsTrigger>
              <TabsTrigger value="people" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                People
              </TabsTrigger>
              <TabsTrigger value="suggested" className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Suggested
              </TabsTrigger>
            </TabsList>

            {/* Language Selector (only show for books tab) */}
            {activeTab === 'books' && (
              <div className="flex justify-center gap-2 mt-4">
                <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                  <SelectTrigger className="w-[140px] h-9">
                    <SelectValue>
                      {SEARCH_LANGUAGES.find(l => l.code === selectedLanguage)?.flag}{' '}
                      {SEARCH_LANGUAGES.find(l => l.code === selectedLanguage)?.label}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {SEARCH_LANGUAGES.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>
                        {lang.flag} {lang.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Books Tab */}
            <TabsContent value="books" className="space-y-4">
              {searchError ? (
                <Card className="card-elevated">
                  <CardContent className="p-8 text-center space-y-4">
                    <AlertCircle className="w-12 h-12 text-destructive mx-auto" />
                    <h3 className="font-semibold">{searchError}</h3>
                    <Button onClick={retrySearch} variant="outline">
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Try again
                    </Button>
                  </CardContent>
                </Card>
              ) : (loading && bookResults.length === 0) ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Searching local library...</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <Card key={i} className="animate-pulse">
                        <CardContent className="p-0">
                          <div className="aspect-[2/3] bg-muted/20 rounded-t-lg" />
                          <div className="p-4 space-y-2">
                            <div className="h-4 bg-muted/40 rounded" />
                            <div className="h-3 bg-muted/30 rounded w-3/4" />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
               ) : (bookResults.length > 0 || externalBookResults.length > 0) ? (
                <>
                  {/* Local Results Section */}
                  {bookResults.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Book className="h-4 w-4" />
                        <span>{localCount} book{localCount !== 1 ? 's' : ''} in library</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {memoizedBookResults.map((book) => (
                          <div key={book.stableKey} className="space-y-3">
                            <BookCard
                              book={{
                                id: book.id || book.open_library_id || '',
                                title: book.title,
                                subtitle: book.subtitle,
                                authors: book.authors,
                                publishedYear: book.published_year,
                                isbn13: book.isbn13,
                                isbn10: book.isbn10,
                                olid: normalizeOpenLibraryId(book.open_library_id || ''),
                                coverUrl: book.isLocal ? book.cover_url : undefined,
                              }}
                              onClick={() => handleBookClick(book)}
                              showShare={true}
                            />
                            <ReviewTBRButtons 
                              book={book} 
                              onTBRAdd={(bookId) => trackTBRAdd(bookId)}
                              onRatingClick={(bookId) => trackClick(bookId)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Search External Button */}
                  {bookResults.length > 0 && !searchSource.includes('external') && localCount < 10 && (
                    <div className="flex justify-center mt-8 mb-4">
                      <Button
                        onClick={handleSearchExternal}
                        variant="outline"
                        size="lg"
                        className="gap-2"
                        disabled={loadingExternal}
                      >
                        {loadingExternal ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Searching external sources...
                          </>
                        ) : (
                          <>
                            <Globe className="h-4 w-4" />
                            Search on Open Library for more
                          </>
                        )}
                      </Button>
                    </div>
                  )}

                  {/* External Results Section */}
                  {externalBookResults.length > 0 && (
                    <div className="space-y-4 mt-8 pt-8 border-t">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <SearchIcon className="h-4 w-4" />
                        <span>{externalCount} result{externalCount !== 1 ? 's' : ''} from web</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {externalBookResults.map((book, index) => (
                          <div key={book.open_library_id || index} className="space-y-3">
                            <BookCard
                              book={{
                                id: book.id || book.open_library_id || '',
                                title: book.title,
                                subtitle: book.subtitle,
                                authors: book.authors,
                                publishedYear: book.published_year,
                                isbn13: book.isbn13,
                                isbn10: book.isbn10,
                                olid: normalizeOpenLibraryId(book.open_library_id || ''),
                                coverUrl: undefined,
                              }}
                              onClick={() => handleBookClick(book)}
                              showShare={false}
                            />
                            <ReviewTBRButtons 
                              book={book}
                              onTBRAdd={(bookId) => trackTBRAdd(bookId)}
                              onRatingClick={(bookId) => trackClick(bookId)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {isDebug && (
                    <div className="text-xs text-muted-foreground text-center py-2">
                      Page {currentPage}, Local: {localCount}, External: {externalCount}
                    </div>
                  )}
                </>
              ) : query && !loading && bookResults.length === 0 ? (
                <Card className="card-elevated">
                  <CardContent className="p-8 text-center space-y-6">
                    <div>
                      <Book className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="font-semibold mb-2">No books found in local library</h3>
                      <p className="text-muted-foreground mb-4">
                        No results found in our database. Try searching Open Library for more books.
                      </p>
                    </div>
                    
                    {/* External Search Button - Always visible when no local results */}
                    {(!searchSource.includes('external') || externalBookResults.length === 0) && (
                      <Button
                        onClick={handleSearchExternal}
                        variant="default"
                        size="lg"
                        className="gap-2"
                        disabled={loadingExternal}
                      >
                        {loadingExternal ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Searching Open Library...
                          </>
                        ) : (
                          <>
                            <Globe className="h-4 w-4" />
                            Search on Open Library
                          </>
                        )}
                      </Button>
                    )}

                    <div className="text-sm text-muted-foreground space-y-1">
                      <p><strong>Search tips:</strong> "Blue Sisters", "Stephen King", or "9780385547345"</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="card-elevated">
                  <CardContent className="p-8 text-center">
                    <SearchIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-semibold mb-2">Search for books</h3>
                     <p className="text-muted-foreground mb-4">
                       Start typing to search books by title, author, or ISBN. Search automatically detects what you're looking for.
                     </p>
                     <div className="text-sm text-muted-foreground space-y-1">
                       <p><strong>Title:</strong> "Demon Copperhead", "The Seven Moons of Maali Almeida"</p>
                       <p><strong>Author:</strong> "Barbara Kingsolver", "Colson Whitehead"</p>
                       <p><strong>ISBN:</strong> 10 or 13 digit codes for direct book lookup</p>
                     </div>
                  </CardContent>
                </Card>
              )}

               {/* Pagination */}
               {bookResults.length > 0 && !loading && (
                 <div className="flex justify-center pt-6">
                   <Pagination>
                     <PaginationContent>
                       <PaginationItem>
                         <PaginationPrevious 
                           onClick={(e) => {
                             e.preventDefault();
                             if (currentPage > 1) goToPage(currentPage - 1);
                           }}
                           className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                         />
                       </PaginationItem>
                       
                       {/* Show page numbers */}
                       {[...Array(Math.max(1, hasMoreResults ? currentPage + 2 : currentPage))].map((_, idx) => {
                         const pageNum = idx + 1;
                         // Show first page, current page and surrounding pages, and last known page
                         const shouldShow = 
                           pageNum === 1 || 
                           pageNum === currentPage ||
                           Math.abs(pageNum - currentPage) === 1 ||
                           (hasMoreResults && pageNum === currentPage + 2);
                         
                         if (!shouldShow) {
                           // Show ellipsis for gaps
                           if (pageNum === 2 && currentPage > 3) {
                             return (
                               <PaginationItem key={`ellipsis-${pageNum}`}>
                                 <span className="flex h-9 w-9 items-center justify-center text-muted-foreground">...</span>
                               </PaginationItem>
                             );
                           }
                           return null;
                         }
                         
                         return (
                           <PaginationItem key={pageNum}>
                             <PaginationLink
                               onClick={(e) => {
                                 e.preventDefault();
                                 goToPage(pageNum);
                               }}
                               isActive={pageNum === currentPage}
                               className="cursor-pointer"
                             >
                               {pageNum}
                             </PaginationLink>
                           </PaginationItem>
                         );
                       })}
                       
                       <PaginationItem>
                         <PaginationNext 
                           onClick={(e) => {
                             e.preventDefault();
                             if (hasMoreResults) goToPage(currentPage + 1);
                           }}
                           className={!hasMoreResults ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                         />
                       </PaginationItem>
                     </PaginationContent>
                   </Pagination>
                 </div>
               )}
            </TabsContent>

            {/* People Tab */}
            <TabsContent value="people" className="space-y-4">
              {loadingUsers && userResults.length === 0 ? (
                <div className="space-y-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Card key={i} className="animate-pulse">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 bg-muted/40 rounded-full" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-muted/40 rounded w-1/4" />
                          <div className="h-3 bg-muted/30 rounded w-1/3" />
                        </div>
                        <div className="h-8 w-16 bg-muted/40 rounded" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : userResults.length > 0 ? (
                <div className="space-y-4">
                  {userResults.map((person) => (
                    <Card key={person.id} className="card-elevated">
                      <CardContent className="p-4 flex items-center gap-4">
                        <DinoAvatar 
                          src={person.image}
                          name={person.name || person.username}
                          size="lg"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold truncate">
                            {person.name || person.username}
                          </h3>
                          {person.username && person.name && (
                            <p className="text-sm text-muted-foreground truncate">
                              @{person.username}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant={person.isFollowing ? "outline" : "default"}
                            onClick={() => {
                              if (person.isFollowing) {
                                // Unfollow logic (keeping existing)
                                supabase
                                  .from('follows')
                                  .delete()
                                  .eq('follower_id', user?.id)
                                  .eq('following_id', person.id)
                                  .then(() => {
                                    setUserResults(prev => prev.map(u => 
                                      u.id === person.id ? { ...u, isFollowing: false } : u
                                    ));
                                  });
                              } else {
                                handleFollowToggle(person);
                              }
                            }}
                          >
                            {person.isFollowing ? 'Following' : 'Follow'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/u/${person.username || person.id}`)}
                          >
                            <User className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : query && !loading ? (
                <Card className="card-elevated">
                  <CardContent className="p-8 text-center">
                    <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-semibold mb-2">No users found</h3>
                    <p className="text-muted-foreground">
                      Try searching with different keywords.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <Card className="card-elevated">
                  <CardContent className="p-8 text-center">
                    <SearchIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-semibold mb-2">Search for people</h3>
                    <p className="text-muted-foreground">
                      Find other readers by name or username.
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Suggested Tab */}
            <TabsContent value="suggested" className="space-y-6">
              {recommendationsLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                  <p className="text-muted-foreground mt-4">Finding people you might like...</p>
                </div>
              ) : (
                <>
                  {/* Friends of Friends */}
                  {recommendations?.mutualFollows && recommendations.mutualFollows.length > 0 && (
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-lg font-semibold">Friends of Friends</h3>
                        <p className="text-sm text-muted-foreground">People followed by readers you follow</p>
                      </div>
                      <div className="grid gap-4">
                        {recommendations.mutualFollows.map((person: any) => (
                          <Card key={person.id} className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <button onClick={() => navigate(`/u/${person.username}`)}>
                                  <DinoAvatar
                                    src={person.image}
                                    name={person.username || 'User'}
                                    size="md"
                                    className="cursor-pointer"
                                  />
                                </button>
                                <div>
                                  <button
                                    onClick={() => navigate(`/u/${person.username}`)}
                                    className="font-medium hover:underline text-left"
                                  >
                                    {person.name || person.username}
                                  </button>
                                  <p className="text-sm text-muted-foreground">
                                    @{person.username}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {person.mutual_count} mutual {person.mutual_count === 1 ? 'connection' : 'connections'}
                                  </p>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant={person.isFollowing ? "outline" : "default"}
                                onClick={() => handleFollowToggle(person)}
                                disabled={followingLoading[person.id]}
                              >
                                {followingLoading[person.id] ? 'Loading...' : person.isFollowing ? 'Following' : 'Follow'}
                              </Button>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Similar Readers */}
                  {recommendations?.similarReaders && recommendations.similarReaders.length > 0 && (
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-lg font-semibold">Similar Readers</h3>
                        <p className="text-sm text-muted-foreground">People who love the same books as you</p>
                      </div>
                      <div className="grid gap-4">
                        {recommendations.similarReaders.map((person: any) => (
                          <Card key={person.id} className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <button onClick={() => navigate(`/u/${person.username}`)}>
                                  <DinoAvatar
                                    src={person.image}
                                    name={person.username || 'User'}
                                    size="md"
                                    className="cursor-pointer"
                                  />
                                </button>
                                <div>
                                  <button
                                    onClick={() => navigate(`/u/${person.username}`)}
                                    className="font-medium hover:underline text-left"
                                  >
                                    {person.name || person.username}
                                  </button>
                                  <p className="text-sm text-muted-foreground">
                                    @{person.username}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {person.shared_books_count} shared {person.shared_books_count === 1 ? 'favorite' : 'favorites'}
                                  </p>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant={person.isFollowing ? "outline" : "default"}
                                onClick={() => handleFollowToggle(person)}
                                disabled={followingLoading[person.id]}
                              >
                                {followingLoading[person.id] ? 'Loading...' : person.isFollowing ? 'Following' : 'Follow'}
                              </Button>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Popular Users */}
                  {recommendations?.trendingUsers && recommendations.trendingUsers.length > 0 && (
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-lg font-semibold">Popular Readers</h3>
                        <p className="text-sm text-muted-foreground">Well-followed members of the community</p>
                      </div>
                      <div className="grid gap-4">
                        {recommendations.trendingUsers.map((person: any) => (
                          <Card key={person.id} className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <button onClick={() => navigate(`/u/${person.username}`)}>
                                  <DinoAvatar
                                    src={person.image}
                                    name={person.username || 'User'}
                                    size="md"
                                    className="cursor-pointer"
                                  />
                                </button>
                                <div>
                                  <button
                                    onClick={() => navigate(`/u/${person.username}`)}
                                    className="font-medium hover:underline text-left"
                                  >
                                    {person.name || person.username}
                                  </button>
                                  <p className="text-sm text-muted-foreground">
                                    @{person.username}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {person.follower_count} {person.follower_count === 1 ? 'follower' : 'followers'}
                                  </p>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant={person.isFollowing ? "outline" : "default"}
                                onClick={() => handleFollowToggle(person)}
                                disabled={followingLoading[person.id]}
                              >
                                {followingLoading[person.id] ? 'Loading...' : person.isFollowing ? 'Following' : 'Follow'}
                              </Button>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Empty state */}
                  {(!recommendations?.mutualFollows?.length && 
                    !recommendations?.similarReaders?.length && 
                    !recommendations?.trendingUsers?.length) && (
                    <div className="text-center py-12">
                      <Sparkles className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        No suggestions available yet. Start following people or rating books to get personalized recommendations!
                      </p>
                    </div>
                  )}
                </>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}